import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Tooltip } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import { userSelector } from '../../reducers/current-user-selectors';
import { loadEmployeeGroups } from '../actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../employee-group-selectors';

import { UiButton } from '../../ui/ui-button';

import { isIntegrationGroup } from '../../util/is-integration-group';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { sortAlphabetically, SORT_ORDER } from '../../util/sort-functions';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  orgEmployeeGroups: employeeGroupsForOrgIdSelector(state, orgId),
  user: userSelector(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
  },
});

type InputProps = {
  addButtonLabel?: string;
  onAdd?: (employeeGroupId: number) => void;
  onRemove?: (employeeGroupId: number) => void;
  onCreate?: (groupName: string) => void;
  employeeGroupIds: Array<number>;
  orgId: number;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeGroupsBase = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    addButtonLabel,
    onAdd,
    onRemove,
    onCreate,
    employeeGroupIds,
    orgEmployeeGroups,
    user,
    actions,
  }: Props) => {
    const hasFullEmployeePermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

    const [groupSelectorVisible, setGroupSelectorVisible] = useState<boolean>(false);
    const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [groupNameError, setGroupNameError] = useState<string | null>(null);

    useEffect(() => {
      actions.loadOrgEmployeeGroups();
    }, [actions]);

    const handleSelect = (
      event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
    ) => {
      if (event.target.value === 'new') {
        toggleCreatingGroup();
      } else {
        handleAddToGroup(event.target.value as any);
      }
    };

    const handleAddToGroup = (employeeGroupId: number) => {
      if (onAdd) {
        onAdd(employeeGroupId);
      }
    };

    const handleRemoveFromGroup = (employeeGroupId: number) => () => {
      if (onRemove) {
        onRemove(employeeGroupId);
      }
    };

    const toggleCreatingGroup = () => {
      setNewGroupName('');
      setGroupNameError(null);
      setIsCreatingGroup(!isCreatingGroup);
    };

    const toggleGroupSelector = () => {
      setGroupSelectorVisible(!groupSelectorVisible);
    };

    const handleGroupNameChange = (
      e: React.ChangeEvent<{ name?: string | undefined; value: string }>,
    ) => {
      const groupNameInput = e.target.value;
      let groupNameInputError = orgEmployeeGroups.find(
        (group) => group.name.toLocaleLowerCase() === groupNameInput.toLocaleLowerCase(),
      )
        ? 'A group with this name already exists (group names are case insensitive)'
        : null;
      if (!groupNameInputError && groupNameInput === '') {
        groupNameInputError = 'Please enter a group name';
      }

      setNewGroupName(groupNameInput);
      setGroupNameError(groupNameInputError);
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (groupNameError) {
        return;
      }
      if (e.keyCode === 13) {
        await handleCreateGroup();
      }
    };

    const handleCreateGroup = async () => {
      if (onCreate) {
        onCreate(newGroupName);
      }
      toggleCreatingGroup();
    };

    if (!employeeGroupIds || !orgEmployeeGroups) {
      return null;
    }

    // Show (read-only) groups from integrations first, then groups added in TE
    const orderedGroups = orgEmployeeGroups.sort((a: EmployeeGroup, b: EmployeeGroup) => {
      const cmp = +isIntegrationGroup(b) - +isIntegrationGroup(a);
      return (
        cmp ||
        sortAlphabetically((group: EmployeeGroup) => group.name.toLowerCase(), SORT_ORDER.ASC)(a, b)
      );
    });
    // Exclude groups imported from an integration in the selectable groups
    const unselectedGroups = orderedGroups.filter(
      (g) => !employeeGroupIds.includes(g.employeeGroupId) && !isIntegrationGroup(g),
    );
    const selectedGroups = orderedGroups.filter((g) =>
      employeeGroupIds.includes(g.employeeGroupId),
    );

    return (
      <>
        <div className="u-flex-center u-flexWrap-wrap">
          {selectedGroups.map((g) =>
            isIntegrationGroup(g) ? (
              <Tooltip
                title="Edit imported groups in your HR/Payroll system."
                key={g.employeeGroupId}
              >
                <Chip
                  className="u-margin-top-sm u-margin-right-sm"
                  key={g.employeeGroupId}
                  size="small"
                  color="primary"
                  label={g.name}
                />
              </Tooltip>
            ) : (
              <Chip
                className="u-margin-top-sm u-margin-right-sm"
                key={g.employeeGroupId}
                size="small"
                color="primary"
                label={g.name}
                onDelete={onRemove ? handleRemoveFromGroup(g.employeeGroupId) : undefined}
              />
            ),
          )}
          {groupSelectorVisible && (
            <Select open onClose={toggleGroupSelector} onChange={handleSelect} value="">
              {unselectedGroups.map((group) => (
                <MenuItem key={group.employeeGroupId} value={group.employeeGroupId}>
                  {group.name}
                </MenuItem>
              ))}
              {hasFullEmployeePermission && onCreate && (
                <MenuItem value="new">
                  <UiButton size="sm">+ Create New Group</UiButton>
                </MenuItem>
              )}
            </Select>
          )}
          {!groupSelectorVisible &&
            (hasFullEmployeePermission || unselectedGroups.length > 0) &&
            onAdd && (
              <UiButton
                className="u-margin-top-sm"
                variant="tertiary"
                size="xxs"
                onClick={toggleGroupSelector}
              >
                {addButtonLabel || `Add${selectedGroups.length === 0 ? ' To Group' : ''}`}
              </UiButton>
            )}
          {!hasFullEmployeePermission && orgEmployeeGroups.length === 0 && (
            <div className="typo-subtitle">Not in any groups</div>
          )}
        </div>
        <div className="u-flex-center">
          {isCreatingGroup ? (
            <>
              <TextField
                autoFocus
                onKeyDown={handleKeyDown}
                onChange={handleGroupNameChange}
                value={newGroupName}
                variant="outlined"
                margin="dense"
              />
              <UiButton
                className="u-margin-left-sm u-margin-right-sm"
                size="sm"
                onClick={handleCreateGroup}
                disabled={Boolean(groupNameError) || newGroupName === ''}
              >
                Create
              </UiButton>
              <UiButton variant="dismiss" size="sm" onClick={toggleCreatingGroup}>
                Cancel
              </UiButton>
            </>
          ) : null}
        </div>
        {groupNameError ? <div className="typo-subtitle u-color-red">{groupNameError}</div> : null}
      </>
    );
  },
);
